import React from 'react';

import classNames from 'classnames';
import { EmblaCarouselType } from 'embla-carousel';
import useEmblaCarousel from 'embla-carousel-react';

import { ImageSize } from '@nxte-nl/image-hooks';

import { hydrationHooks } from '../../hooks/hydrationHooks/hydrationHooks';
import { ImagePresentational } from '../../types/common';
import { ImageWithSize } from '../ImageWithSize/ImageWithSize';
import styles from './EmblaSliderThumbs.module.css';

export type Props = {
  children: ImagePresentational[];
  containerClassName?: string;
  emblaApi?: EmblaCarouselType;
};

export type UseCreateEmblaSliderParams = {
  emblaApi?: EmblaCarouselType;
};

type UseHandleOnClickParams = {
  emblaThumbsApi?: EmblaCarouselType;
};

export const hooks = {
  useCreateEmblaSliderThumbs({ emblaApi }: UseCreateEmblaSliderParams) {
    const [emblaThumbsRef, emblaThumbsApi] = useEmblaCarousel({
      align: 'start',
      containScroll: 'trimSnaps',
      loop: true,
    });

    const syncThumbs = React.useCallback(() => {
      const selectedMainIndex = emblaApi?.selectedScrollSnap();
      selectedMainIndex && emblaThumbsApi?.scrollTo(selectedMainIndex, false);
    }, [emblaApi, emblaThumbsApi]);

    const syncMain = React.useCallback(() => {
      const selectedThumbsIndex = emblaThumbsApi?.selectedScrollSnap();
      selectedThumbsIndex && emblaApi?.scrollTo(selectedThumbsIndex);
    }, [emblaApi, emblaThumbsApi]);

    React.useEffect(() => {
      emblaApi?.on('select', syncThumbs);
      emblaThumbsApi?.on('select', syncMain);
      return () => {
        emblaApi?.off('select', syncThumbs);
        emblaThumbsApi?.off('select', syncMain);
      };
    }, [emblaApi, emblaThumbsApi, syncMain, syncThumbs]);

    return {
      emblaThumbsRef,
      emblaThumbsApi,
    };
  },

  useHandleOnClick ({
    emblaThumbsApi,
  }: UseHandleOnClickParams) {
    return React.useCallback(
      (index: number) => () => {
        emblaThumbsApi?.scrollTo(index);
      }, [
        emblaThumbsApi,
      ],
    );
  },
};

export const EmblaSliderThumbs = React.memo((props: Props) => {
  const {
    children,
    emblaApi,
  } = props;

  const {
    emblaThumbsRef,
    emblaThumbsApi,
  } = hooks.useCreateEmblaSliderThumbs({
    emblaApi,
  });

  const handleOnClick = hooks.useHandleOnClick({ emblaThumbsApi });

  const { isClient } = hydrationHooks.useIsClient();

  return (
    <div ref={emblaThumbsRef}
      className={styles.emblaThumbnails}
    >
      <div
        className={classNames(styles.emblaThumbnailsContainer)}
      >
        {children.map((thumbnail, index)=> (
          <div
            key={thumbnail.url.href}
            onClick={handleOnClick(index)}
            className={classNames(styles.emblaThumbnail, {
              [styles.disabled]: !isClient,
            })}
          >
            <ImageWithSize
              image={thumbnail}
              size={ImageSize.S}
            />
          </div>
        ))}
      </div>
    </div>
  );
});
